/**
 * Survey system — API contract.
 *
 * Mirrors the live `[ADMIN]` endpoints in the SURVEYS collection of the Apidog project
 * (`/api/admin/surveys/*`). Verified against the published OpenAPI spec; where this file
 * and the spec disagree, the spec wins.
 */

/* -------------------------------------------------------------------------- */
/* Questions                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * `string` fallback on every union: the backend may add cases before we do, and an
 * unknown value should render as "unknown", not crash the table.
 */
export type SurveyQuestionType =
  | "SINGLE_CHOICE"
  | "MULTI_CHOICE"
  | "SHORT_TEXT"
  | "LONG_TEXT"
  | "NPS"
  | "RATING"
  | string;

export type SurveyQuestionOption = {
  /** Stable within the question. Responses store this, never the label. */
  id: string;
  label: string;
};

/**
 * A tap-to-insert answer chip on a text question. Shaped exactly like an option: answers
 * record the `id`, so re-wording a chip never orphans the usage counts behind it.
 */
export type SurveyQuestionSuggestion = SurveyQuestionOption;

export type SurveyQuestion = {
  /**
   * Stable slug (e.g. `primary_use`). This — not the array index — is what a response's
   * `questionKey` points at, so reordering or re-wording a question never orphans data.
   * Immutable once the survey has been published.
   */
  key: string;
  prompt: string;
  /** Optional helper line under the prompt. */
  helpText?: string | null;
  type: SurveyQuestionType;
  order: number;
  required: boolean;

  /* Choice types (SINGLE_CHOICE, MULTI_CHOICE) */
  options?: SurveyQuestionOption[];
  /** Appends an "Other" choice that reveals a free-text field when picked. */
  allowOther?: boolean;
  otherPlaceholder?: string | null;
  /** MULTI_CHOICE only. `null` = no limit. */
  maxSelections?: number | null;

  /* Text types (SHORT_TEXT, LONG_TEXT) */
  placeholder?: string | null;
  maxLength?: number | null;
  /**
   * Tap-to-insert answer chips shown under the textarea. Appended to whatever the user has
   * already typed, not substituted for it — matches the designer's prototype.
   *
   * The server assigns each `id` from the label when one is not sent.
   */
  suggestions?: SurveyQuestionSuggestion[];

  /* Scale types (NPS, RATING) */
  scale?: {
    min: number;
    max: number;
    minLabel?: string | null;
    maxLabel?: string | null;
  } | null;
};

/* -------------------------------------------------------------------------- */
/* Survey                                                                      */
/* -------------------------------------------------------------------------- */

export type SurveyStatus = "DRAFT" | "SCHEDULED" | "ACTIVE" | "CLOSED" | string;

/**
 * The bucket the API uses for "Other" — as an answer `value` and as a `breakdown` row id.
 */
export const OTHER_OPTION_ID = "__other__";

/**
 * Who is eligible to be shown the survey.
 *
 * NOTE: the published spec only ever documents `"ALL"`, and the builder exposes no audience
 * picker, so that default is all we send today. The other members here are unconfirmed —
 * check with the backend before building a picker on top of them.
 */
export type SurveyAudience =
  | "ALL"
  | "NEW_USERS"
  | "EXISTING_USERS"
  | "KYC_VERIFIED"
  | string;

/** Where in the user-facing apps the CTA appears. */
export type SurveyPlacement =
  | "DASHBOARD_CTA"
  | "SETTINGS"
  | "TRANSACTION_SUCCESS"
  | string;

/** Denormalised counters, so the list and detail header need no extra round trip. */
export type SurveyStats = {
  /** CTA impressions. */
  views: number;
  /** Users who opened the survey. */
  starts: number;
  completions: number;
  /** Started but abandoned. */
  partials: number;
  /** `completions / starts`, already a percentage (0–100). */
  completionRate: number;
  /** Mean seconds from first question to submit, over completed responses. */
  avgDurationSeconds: number | null;
};

export type SurveyApi = {
  _id: string;
  title: string;
  /** Intro-screen body copy. */
  description?: string | null;
  status: SurveyStatus;
  audience: SurveyAudience;
  placements: SurveyPlacement[];
  questions: SurveyQuestion[];

  startsAt: string | null;
  endsAt: string | null;
  /** Strips user identity from responses. Locks the Responses tab to aggregates only. */
  isAnonymous: boolean;
  allowMultipleResponses: boolean;
  /** Points credited on completion. `null`/0 = no reward. */
  rewardPoints?: number | null;

  stats: SurveyStats;

  createdBy?: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string | null;
  closedAt?: string | null;
};

export type SurveysPagination = {
  page: number;
  limit: number;
  total: number;
  pages: number;
};

export type SurveysListPayload = {
  surveys: SurveyApi[];
  pagination: SurveysPagination;
};

export type SurveysListEnvelope = {
  status: string;
  data: SurveysListPayload;
};

export type SurveyDetailEnvelope = {
  status: string;
  data: SurveyApi;
};

/* -------------------------------------------------------------------------- */
/* Metrics (section header cards)                                              */
/* -------------------------------------------------------------------------- */

export type SurveysMetricsPayload = {
  totalSurveys: number;
  activeSurveys: number;
  totalResponses: number;
  /** Across all surveys, 0–100. */
  avgCompletionRate: number;
};

export type SurveysMetricsEnvelope = {
  status: string;
  data: SurveysMetricsPayload;
};

/* -------------------------------------------------------------------------- */
/* Responses                                                                   */
/* -------------------------------------------------------------------------- */

export type SurveyResponseStatus = "COMPLETED" | "PARTIAL" | string;

export type SurveyAnswer = {
  questionKey: string;
  type: SurveyQuestionType;
  /**
   * Shape follows `type`:
   * - SINGLE_CHOICE → option id (`string`); the literal `OTHER_OPTION_ID` when the user
   *   picked "Other", with the text in `otherText`
   * - MULTI_CHOICE  → option ids (`string[]`)
   * - SHORT_TEXT / LONG_TEXT → the typed text (`string`)
   * - NPS / RATING  → the number picked (`number`)
   */
  value: string | string[] | number | null;
  /** Free text captured when the user picked "Other". */
  otherText?: string | null;
  /**
   * Which suggestion chips the user tapped, for text answers — `id`s, not labels. Resolve
   * them against the question's `suggestions` to display. Lets us report "70% of complaints
   * started from 'Transactions take too long'" separately from free typing.
   */
  usedSuggestions?: string[];
};

export type SurveyRespondent = {
  _id: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

export type SurveyResponseApi = {
  _id: string;
  surveyId: string;
  /** `null` when the survey is anonymous. */
  user: SurveyRespondent | null;
  status: SurveyResponseStatus;
  /** Which CTA the user came in from. */
  placement: SurveyPlacement | null;
  startedAt: string;
  submittedAt: string | null;
  durationSeconds: number | null;
  answers: SurveyAnswer[];
};

export type SurveyResponsesPayload = {
  responses: SurveyResponseApi[];
  pagination: SurveysPagination;
};

export type SurveyResponsesEnvelope = {
  status: string;
  data: SurveyResponsesPayload;
};

/* -------------------------------------------------------------------------- */
/* Summary (server-side aggregation)                                           */
/* -------------------------------------------------------------------------- */

/** One row of a choice/scale breakdown. */
export type SurveyBreakdownItem = {
  /** Option id, or the literal `"__other__"` for the Other bucket. */
  id: string;
  label: string;
  count: number;
  /** Share of `answered`, 0–100. */
  percentage: number;
};

/** A sampled free-text answer, for the text-question feed. */
export type SurveyTextSample = {
  responseId: string;
  text: string;
  submittedAt: string | null;
  user: SurveyRespondent | null;
};

export type SurveyQuestionSummary = {
  questionKey: string;
  prompt: string;
  type: SurveyQuestionType;
  /** Responses that answered this question. */
  answered: number;
  skipped: number;

  /* Choice types */
  breakdown?: SurveyBreakdownItem[];
  /** Free text behind the "Other" bucket, most recent first. */
  otherTexts?: SurveyTextSample[];

  /* Text types */
  /** Most recent first, capped server-side (see the .md for the cap). */
  textSamples?: SurveyTextSample[];
  /** How often each configured suggestion chip was used; `id` is the suggestion id. */
  suggestionUsage?: SurveyBreakdownItem[];

  /* NPS */
  nps?: {
    /** promoters% − detractors%, −100..100. */
    score: number;
    promoters: number;
    passives: number;
    detractors: number;
    /** Count per value 0–10, index = score. */
    distribution: number[];
  };

  /* RATING */
  rating?: {
    average: number;
    /** Count per point on the scale, ascending. */
    distribution: number[];
  };
};

export type SurveySummaryPayload = {
  surveyId: string;
  stats: SurveyStats;
  questions: SurveyQuestionSummary[];
};

export type SurveySummaryEnvelope = {
  status: string;
  data: SurveySummaryPayload;
};

/* -------------------------------------------------------------------------- */
/* Mutations                                                                   */
/* -------------------------------------------------------------------------- */

/** Body for POST api/admin/surveys and PATCH api/admin/surveys/:id. */
export type SurveyWriteBody = {
  title: string;
  description?: string | null;
  audience?: SurveyAudience;
  placements?: SurveyPlacement[];
  questions: SurveyQuestion[];
  startsAt?: string | null;
  endsAt?: string | null;
  isAnonymous?: boolean;
  allowMultipleResponses?: boolean;
  rewardPoints?: number | null;
};

export type SurveyMutationEnvelope = {
  status: string;
  data?: SurveyApi;
  message?: string;
};
